import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { Warning } from '@/components/Warning';
import { DateUtils } from '@/utils/dateUtils';

import useViewerStore, { SidebarLeftTab } from '../-utils/useViewerStore';
import { useVigerendDataContext } from './-components/VigerendDataContext';
import { VigerendObjectList } from './-components/VigerendObjectList';
import { VigerendPastWarning } from './-components/VigerendPastWarning';
import { useFindHoofdstukInVigerendData } from './-utils/useFindHoofdstukInVigerendData';
import { ChapterNavigation } from '../-components/ChapterNavigation';
import { GRONDSLAGEN_MAP } from '@/constants/grondslagen';
import { VigerendContentHeader } from './-components/VigerendContentHeader';

export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/vigerend/inhoud/$hoofdstukLineageId'
)({
  validateSearch: z.object({
    datum: z.string().date().catch(DateUtils.formatForInput(new Date())),
    focusParagraafLineageId: z.string().optional(),
  }),
  onEnter: () => {
    useViewerStore.getState().setSidebarLeftTabId(SidebarLeftTab.INHOUD);
  },
  component: InhoudComponent,
});

function InhoudComponent() {
  const { bedrijfLineageId, hoofdstukLineageId } = Route.useParams();
  const { datum, focusParagraafLineageId, datumObject } = Route.useSearch({
    select: (search) => ({
      datum: search.datum,
      focusParagraafLineageId: search.focusParagraafLineageId,
      datumObject: new Date(search.datum),
    }),
  });

  const scrollToParagraaf = useViewerStore((s) => s.scrollToParagraaf);
  const { allGrondslagen } = useVigerendDataContext();

  const {
    hoofdstuk: currentHoofdstuk,
    nextHoofdstuk,
    previousHoofdstuk,
  } = useFindHoofdstukInVigerendData({
    vigerendData: allGrondslagen,
    hoofdstukLineageId: hoofdstukLineageId,
  });

  return (
    <>
      <VigerendPastWarning
        date={datumObject}
        bedrijfLineageId={bedrijfLineageId}
        hoofdstukLineageId={hoofdstukLineageId}
        view="inhoud"
      />

      {!currentHoofdstuk ? (
        <Warning variant="info" size="large">
          <Warning.Title>Geen resultaten</Warning.Title>
          <Warning.Description>
            Het huidige hoofdstuk heeft geen voorschriften
          </Warning.Description>
        </Warning>
      ) : (
        <>
          {datumObject && (
            <VigerendContentHeader
              date={datumObject}
              jumpToProps={{
                hoofdstukNummer: currentHoofdstuk.data.Nummer,
                paragrafen: currentHoofdstuk.paragrafen,
                onJumpToParagraaf: (index: number) => {
                  const paragraaf = currentHoofdstuk.paragrafen[index];

                  if (!paragraaf || !scrollToParagraaf) return;

                  scrollToParagraaf(paragraaf.data.ID);
                },
              }}
            />
          )}

          <VigerendObjectList
            vigerendHoofdstuk={currentHoofdstuk}
            focusParagraafLineageId={focusParagraafLineageId}
          />
        </>
      )}

      <ChapterNavigation
        previous={
          previousHoofdstuk?.data.ID && (
            <ChapterNavigation.Previous
              subtitle="Vorig hoofdstuk"
              grondslag={GRONDSLAGEN_MAP.get(previousHoofdstuk.data.Grondslag)}
              nummer={previousHoofdstuk.data.Nummer}
              htmlTitle={previousHoofdstuk.data.Titel}
              to="/$bedrijfLineageId/viewer/vigerend/inhoud/$hoofdstukLineageId"
              params={{
                bedrijfLineageId,
                hoofdstukLineageId: previousHoofdstuk.data.Lineage,
              }}
              search={{ datum }}
            />
          )
        }
        next={
          nextHoofdstuk?.data.ID && (
            <ChapterNavigation.Next
              grondslag={GRONDSLAGEN_MAP.get(nextHoofdstuk.data.Grondslag)}
              subtitle="Volgend hoofdstuk"
              nummer={nextHoofdstuk.data.Nummer}
              htmlTitle={nextHoofdstuk.data.Titel}
              to="/$bedrijfLineageId/viewer/vigerend/inhoud/$hoofdstukLineageId"
              params={{
                bedrijfLineageId,
                hoofdstukLineageId: nextHoofdstuk.data.Lineage,
              }}
              search={{ datum }}
            />
          )
        }
      />
    </>
  );
}
